/**
 * Slideout menu for mobile navigation (<768px).
 * Toggles the burger button and the slideout drawer + overlay.
 */

/**
 * Collapse the submenus in the slideout into accordions.
 *
 * wp_nav_menu renders a parent as a plain link followed by its <ul>, so every
 * level would sit permanently open. The parent items point at real pages, so
 * the link stays tappable and the chevron next to it becomes the toggle.
 */
function initSlideoutAccordion(slideout) {
  const menu = slideout.querySelector('.ecw-slideout__menu');
  if (!menu) return;

  const parents = menu.querySelectorAll('.menu-item-has-children');
  if (!parents.length) return;

  parents.forEach((item, index) => {
    const link    = item.querySelector(':scope > a');
    const submenu = item.querySelector(':scope > .sub-menu');
    if (!link || !submenu) return;

    if (!submenu.id) submenu.id = `ecw-slideout-submenu-${index + 1}`;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'ecw-slideout__submenu-toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', submenu.id);
    toggle.setAttribute('aria-label', `${link.textContent.trim()} submenu`);
    toggle.innerHTML =
      '<svg class="ecw-slideout__chevron" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M7.5 15L12.5 10L7.5 5" stroke="#858585" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';

    toggle.addEventListener('click', () => {
      const willOpen = !item.classList.contains('is-open');
      item.classList.toggle('is-open', willOpen);
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });

    link.insertAdjacentElement('afterend', toggle);
  });

  // Only hide the submenus once the toggles exist (see _slideout.scss).
  menu.classList.add('is-collapsible');
}

/**
 * Collapse every open section, so the menu always reopens in the state the
 * design shows: top-level items only.
 */
function collapseSlideoutAccordion(slideout) {
  slideout.querySelectorAll('.ecw-slideout__menu .is-open').forEach((item) => {
    item.classList.remove('is-open');
    item
      .querySelector(':scope > .ecw-slideout__submenu-toggle')
      ?.setAttribute('aria-expanded', 'false');
  });
}

export function initSlideoutMenu() {
  const toggle  = document.querySelector('.ecw-burger');
  const closeBtn = document.querySelector('.ecw-slideout__close');
  const slideout = document.querySelector('.ecw-slideout');
  const overlay  = document.querySelector('.ecw-slideout-overlay');

  if (!toggle || !slideout || !overlay) return;

  initSlideoutAccordion(slideout);

  const open = () => {
    slideout.classList.add('is-open');
    overlay.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('ecw-nav-open');
    // Focus first focusable element in slideout
    const firstLink = slideout.querySelector('a, button');
    firstLink?.focus();
  };

  const close = () => {
    slideout.classList.remove('is-open');
    overlay.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('ecw-nav-open');
    collapseSlideoutAccordion(slideout);
    toggle.focus();
  };

  toggle.addEventListener('click', () => {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    isOpen ? close() : open();
  });

  closeBtn?.addEventListener('click', close);
  overlay.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && slideout.classList.contains('is-open')) close();
  });

  // Close if viewport grows past breakpoint
  const mq = window.matchMedia('(min-width: 768px)');
  mq.addEventListener('change', (e) => { if (e.matches) close(); });
}
